import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { PasswordInput } from './ui/password-input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Users, UserPlus, Trash2, Shield, Mail, Loader2 } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { PendingStakeholderInvitations } from './PendingStakeholderInvitations';

interface User {
  id: string;
  email: string;
  name?: string;
  team: string | null;
  isCompanyAdmin: boolean;
  status: string;
  createdAt: string;
}

interface CompanyUsersProps {
  accessToken: string;
  currentUser: any;
  companyId?: string; // Optional - if not provided, uses currentUser's companyId
}

export function CompanyUsers({ accessToken, currentUser, companyId }: CompanyUsersProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [companyName, setCompanyName] = useState('');

  // Add user form state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserTeam, setNewUserTeam] = useState('');
  const [newUserIsCompanyAdmin, setNewUserIsCompanyAdmin] = useState(false);

  // Edit user form state
  const [editUserTeam, setEditUserTeam] = useState('');
  const [editUserIsCompanyAdmin, setEditUserIsCompanyAdmin] = useState(false);

  // Resend onboarding state
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
    loadCompanyInfo();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const targetCompanyId = companyId || currentUser.companyId;
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${targetCompanyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCompanyName(data.name || '');
      }
    } catch (err) {
      console.error('Error loading company info:', err);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      // Use different endpoint depending on whether companyId is provided (consultant viewing client) or not (company admin viewing own company)
      const endpoint = companyId 
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${companyId}/users`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/users`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load users');
      }

      const data = await response.json();
      setUsers(data.users || []);

    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    setError('');

    try {
      const requestBody: any = {
        email: newUserEmail,
        name: newUserName,
        password: newUserPassword,
        userTeam: newUserTeam || null,
        isCompanyAdmin: newUserIsCompanyAdmin,
      };

      // If companyId is provided (consultant viewing client), include it
      if (companyId) {
        requestBody.companyId = companyId;
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/add-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add user');
      }

      // Reset form
      setNewUserEmail('');
      setNewUserName('');
      setNewUserPassword('');
      setNewUserTeam('');
      setNewUserIsCompanyAdmin(false);
      setAddUserDialogOpen(false);

      // Reload users
      await loadUsers();

    } catch (err: any) {
      console.error('Error adding user:', err);
      setError(err.message || 'Failed to add user');
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/update-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            userId: selectedUser.id,
            updates: {
              team: editUserTeam || null,
              isCompanyAdmin: editUserIsCompanyAdmin,
            },
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update user');
      }

      setEditUserDialogOpen(false);
      setSelectedUser(null);

      // Reload users
      await loadUsers();

    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user');
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this user? This action cannot be undone.')) {
      return;
    }

    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/remove-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove user');
      }

      // Reload users
      await loadUsers();

    } catch (err: any) {
      console.error('Error removing user:', err);
      setError(err.message || 'Failed to remove user');
    }
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setEditUserTeam(user.team || '');
    setEditUserIsCompanyAdmin(user.isCompanyAdmin);
    setEditUserDialogOpen(true);
  };

  const handleResendOnboarding = async (userId: string) => {
    setResendingUserId(userId);
    setResendSuccess(null);
    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/resend-onboarding`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend onboarding email');
      }

      if (data.emailSent) {
        const targetUser = users.find(u => u.id === userId);
        setResendSuccess(targetUser?.email || 'user');
        setTimeout(() => setResendSuccess(null), 5000);
      } else {
        throw new Error(data.error || 'Email could not be sent');
      }
    } catch (err: any) {
      console.error('Error resending onboarding:', err);
      setError(err.message || 'Failed to resend onboarding email');
    } finally {
      setResendingUserId(null);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8" />
            <div>
              <h1>Manage Company Users</h1>
              <p className="text-muted-foreground">
                Add and manage users for your company
              </p>
            </div>
          </div>
          <Button onClick={() => setAddUserDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {resendSuccess && (
          <Alert className="border-green-300 bg-green-50 dark:bg-green-950">
            <Mail className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Onboarding email resent successfully to <strong>{resendSuccess}</strong>
            </AlertDescription>
          </Alert>
        )}

        {/* Pending Stakeholder Invitations */}
        <PendingStakeholderInvitations
          accessToken={accessToken}
          companyId={companyId || currentUser.companyId}
          companyName={companyName}
          userRole={currentUser.role}
          onInviteSent={loadUsers}
        />

        {loading ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Loading users...
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Company Users ({users.length})</CardTitle>
              <CardDescription>
                All users who have access to your company's data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>{user.name || '—'}</div>
                        {!user.name && (
                          <div className="text-muted-foreground">No name set</div>
                        )}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.team || '—'}</TableCell>
                      <TableCell>
                        {user.isCompanyAdmin ? (
                          <Badge variant="secondary">
                            <Shield className="mr-1 h-3 w-3" />
                            Client Engagement Lead
                          </Badge>
                        ) : (
                          <Badge variant="outline">Team Member</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.status === 'verified' || user.status === 'active' ? 'default' : 'secondary'}>
                          {user.status === 'verified' ? 'Verified' : user.status === 'active' ? 'Active' : user.status === 'pending' ? 'Pending' : user.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(user)}
                          >
                            Edit
                          </Button>
                          {user.id !== currentUser.id && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleRemoveUser(user.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          {user.status === 'pending' && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleResendOnboarding(user.id)}
                              disabled={resendingUserId === user.id}
                            >
                              {resendingUserId === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Resend Onboarding'
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Add User Dialog */}
        <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
              <DialogDescription>
                Create a new user account for your company. The user will be able to login immediately with the credentials you provide. 
                Make sure to securely share the email and password with them.
              </DialogDescription>
            </DialogHeader>
            
            <Alert className="bg-blue-50 border-blue-200">
              <Mail className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-sm text-blue-900">
                An onboarding email with a password setup link will be sent automatically when the account is created.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="team">Team (Optional)</Label>
                <Input
                  id="team"
                  type="text"
                  placeholder="e.g., HR, IT, Facilities"
                  value={newUserTeam}
                  onChange={(e) => setNewUserTeam(e.target.value)}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is-company-admin"
                  checked={newUserIsCompanyAdmin}
                  onCheckedChange={(checked) => setNewUserIsCompanyAdmin(checked as boolean)}
                />
                <Label htmlFor="is-company-admin" className="cursor-pointer">
                  Make this user a Client Engagement Lead
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddUserDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddUser}>
                Add User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user information for {selectedUser?.email}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-team">Team</Label>
                <Input
                  id="edit-team"
                  type="text"
                  placeholder="e.g., HR, IT, Facilities"
                  value={editUserTeam}
                  onChange={(e) => setEditUserTeam(e.target.value)}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-is-company-admin"
                  checked={editUserIsCompanyAdmin}
                  onCheckedChange={(checked) => setEditUserIsCompanyAdmin(checked as boolean)}
                />
                <Label htmlFor="edit-is-company-admin" className="cursor-pointer">
                  Client Engagement Lead
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditUser}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}